import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import axios from "axios";
import { getToken } from "../../utils/tokenUtils";
import Modal from "../../components/UI/Modal";
import PayrollComponentForm from "./PayrollComponentForm";
import { useConfigurationComponentType } from "../../contexts/ConfigurationComponentType";
import Notification from "../../components/UI/Notification";
import DropdownMenu from "../../components/UI/DropdownMenu";
import DeleteButton from "../../components/UI/AlertDialog";
import { MoreVertical } from 'lucide-react';

const API_URL = "https://hrmspayroll.prepstripe.com/api";

const defaultForm = {
  component_name: "",
  component_type: "",
  calculation_type: "",
  default_value: "",
  percentage: "",
  formula: "",
  is_taxable: false,
  is_active: true,
  display_order: "",
  description: "",
};

// Reusable actions column (menu) similar to Leave Balance Masters
const ActionsColumn = React.memo(({ row, openMenu, setOpenMenu, onEdit, onDelete, disabled }) => {
  const buttonRef = useRef(null);
  return (
    <div className="flex items-center justify-center relative">
      <button
        ref={buttonRef}
        onClick={() => setOpenMenu(openMenu === row.id ? null : row.id)}
        className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
        disabled={disabled}>
        <MoreVertical className="w-4 h-4 text-gray-600 dark:text-gray-400" />
      </button>

      {openMenu === row.id && (
        <DropdownMenu buttonRef={buttonRef} onClose={() => setOpenMenu(null)}>
          <button
            onClick={() => { onEdit(row.id); setOpenMenu(null); }}
            className="w-full text-left px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
            disabled={disabled}>
            Edit
          </button>
          <DeleteButton
            style={`text-red-600 hover:text-red-800 px-3 py-2`}
            onDelete={() => { onDelete(row.id); setOpenMenu(null); }}
          />
        </DropdownMenu>
      )}
    </div>
  );
});

const PayrollComponents = () => {
  const token = getToken();
  const [notification, setNotification] = useState(null);
  const { component_types, fetchComponentTypes } = useConfigurationComponentType();
  
  // Validate component_types structure
  const validComponentTypes = component_types && Array.isArray(component_types) && 
    component_types.every(type => type && typeof type === 'object' && type.id !== undefined);

  const api = useMemo(() => {
    return axios.create({
      baseURL: API_URL,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });
  }, [token]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [items, setItems] = useState([]);

  // Filters
  const [filterComponentType, setFilterComponentType] = useState("");
  const [filterActive, setFilterActive] = useState("");

  // Types metadata - hardcoded values
  const calculationTypes = ["fixed", "percentage", "formula"];

  // Form state - simplified
  const [formState, setFormState] = useState(defaultForm);
  const [editingId, setEditingId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalTitle, setModalTitle] = useState("Add Component");
  const [formLoading, setFormLoading] = useState(false); 
  const [openMenu, setOpenMenu] = useState(null);

  // Test calculation state
  const [testPayload, setTestPayload] = useState({
    component_id: "",
    basic_salary: "",
    gross_salary: "",
    custom_value: "",
  });

  const loadItems = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = {};
      if (filterComponentType) params.component_type = filterComponentType;
      if (filterActive !== "") params.active = filterActive;
      const res = await api.get(`/payroll/configurations`, { params });
      setItems(Array.isArray(res.data) ? res.data : res.data?.data || []);
    } catch (e) {
      console.error(e);
      setError("Failed to load configurations");
    } finally {
      setLoading(false);
    }
  }, [api, filterActive, filterComponentType]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  useEffect(() => {
    fetchComponentTypes();
  }, []);

  const resetForm = () => {
    setFormState(defaultForm);
    setEditingId(null);
    setModalTitle("Add Component");
  };

  const openCreateModal = () => {
    resetForm();
    setModalTitle("Add Component");
    setShowModal(true);
  };

  const onEdit = async (id) => {
    setError("");
    setFormLoading(true);
    
    try {
      const res = await api.get(`/payroll/configurations/${id}`);
      const cfg = res?.data || {};
      const updatedFormState = {
         component_name: cfg.data.component_name ?? "",
         component_type: typeof cfg.data.component_type === 'object' ? cfg.data.component_type.code : cfg.data.component_type ?? "",
         calculation_type: typeof cfg.data.calculation_type === 'object' ? cfg.data.calculation_type.id : cfg.data.calculation_type ?? "",
         default_value: cfg.data.default_value ?? "",
         percentage: cfg.data.percentage ?? "",
         formula: cfg.data.formula ?? "",
         is_taxable: !!cfg.data.is_taxable,
         is_active: cfg.data.is_active !== false,
         display_order: cfg.data.display_order ?? "",
         description: cfg.data.description ?? "",
      };
      
      // Set all the states together
      setEditingId(id);
      setFormState(updatedFormState);
      setModalTitle("Edit Component");
      setShowModal(true); // Open modal after setting form state
      
    } catch (e) {
      console.error(e);
      setError("Failed to load configuration");
    } finally {
      setFormLoading(false);
    }
  };

  const onDelete = async (id) => {
    // if (!window.confirm("Delete this configuration?")) return;
    setError("");
    try {
      await api.delete(`/payroll/configurations/${id}`);
      
      // Update state by removing the deleted item
      setItems(prevItems => prevItems.filter(item => item.id !== id));
      setNotification({
        message: "COnfiguration deleted successfully",
        type: "success"
      });
    } catch (e) {
      console.error(e);
      setNotification({
        message: "Failed to delete configuration",
        type: "error"
      });
      setError("Failed to delete configuration");
    }
  };

  const onSubmit = async (formData) => {
    setError("");
    try {
      const payload = {
        ...formData,
        // Ensure numeric fields are numbers or omitted if empty
        default_value: formData.default_value === "" ? undefined : Number(formData.default_value),
        percentage: formData.percentage === "" ? undefined : Number(formData.percentage),
        display_order: formData.display_order === "" ? undefined : Number(formData.display_order),
      };
      
      let response;
      if (editingId) {
        response = await api.put(`/payroll/configurations/${editingId}`, payload);
      } else {
        response = await api.post(`/payroll/configurations`, payload);
      }
      
      // Update state with response data instead of refetching
      const newItem = response.data.data;
      
      if (editingId) {
        // Update existing item
        setItems(prevItems => 
          prevItems.map(item => 
            item.id === editingId ? { ...item, ...newItem } : item
          )
        );
        setNotification({
          message: "COnfiguration updated successfully",
          type: "success"
        });
      } else {
        // Add new item to the beginning of the list
        setItems(prevItems => [newItem, ...prevItems]);
        setNotification({
          message: "New configuration added successfully",
          type: "success"
        });
      }
      
      resetForm();
      setShowModal(false);
    } catch (e) {
      console.error(e);
    
      if (e.response && e.response.data && e.response.data.error) {
        const errors = e.response.data.error;
        let errorMessages = "";
    
        if (typeof errors === "string") {
          // Case 2: Single string error
          errorMessages = errors;
        } else if (typeof errors === "object") {
          // Case 1: Field-based errors
          errorMessages = Object.entries(errors)
            .map(([field, messages]) => 
              `${field}: ${Array.isArray(messages) ? messages.join(", ") : messages}`
            )
            .join("\n");
        }
    
        setError(errorMessages);
        setNotification({
          message: errorMessages,
          type: "error"
        });
      } else {
        // Fallback if no structured error is found
        setError("Failed to save configuration");
        setNotification({
          message: "Failed to save configuration",
          type: "error"
        });
      }
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
    resetForm(); 
  };

  return (
    <div className="p-3">

      {notification && (
        <Notification message={notification.message} type={notification.type} onClose={() => setNotification(null)} />
      )}

      {/* Header with Create Button */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-8 gap-4 sm:gap-0">
        <div>
          <h1 className="text-xl sm:text-3xl font-bold text-gray-900">Payroll Components</h1>
        </div>
        <button
          onClick={openCreateModal}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center justify-center hover:bg-blue-700 w-full sm:w-auto">
          + Add Component
        </button>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-6 gap-4 mb-6">
        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium dark:text-white">Component Type</label>
          <select
            className="border rounded-lg px-3 py-2 bg-white dark:bg-gray-900 dark:text-white"
            value={filterComponentType}
            onChange={(e) => setFilterComponentType(e.target.value)}
            disabled={!validComponentTypes || component_types.length === 0}>
            <option value="">All types</option>
                         {validComponentTypes && component_types.length > 0 ? (
               component_types.map((opt) => (
                 <option key={opt.id} value={opt.code}>{opt.name || opt.code || opt.id}</option>
               ))
             ) : (
               <option value="" disabled>{!component_types ? 'Loading...' : 'Invalid data'}</option>
             )}
          </select>
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium dark:text-white">Active</label>
          <select
            className="border rounded-lg px-3 py-2 bg-white dark:bg-gray-900 dark:text-white"
            value={filterActive}
            onChange={(e) => setFilterActive(e.target.value)}>
            <option value="">Any</option>
            <option value="true">True</option>
            <option value="false">False</option>
          </select>
        </div>
        {((filterActive === 'true') || (filterActive === 'false') || filterComponentType) && (
          <div className="flex items-end">
            <button
              onClick={() => { setFilterComponentType(""); setFilterActive(""); }}
              className="ml-2 px-4 py-2 bg-gray-200 dark:bg-gray-800 dark:text-white rounded hover:bg-gray-300 dark:hover:bg-gray-700"
            >
              Reset
            </button>
          </div>
        )}
      </div>

      {/* Modal for Create/Edit */}
      <Modal
        isOpen={showModal}
        onRequestClose={handleModalClose}
        title={modalTitle}>
        {formLoading ? (
          <div className="p-2 text-center">Loading form data...</div>
        ) : (
          <PayrollComponentForm
            key={`${editingId || 'create'}-${showModal}`}
            closeModal={handleModalClose}
            onSubmit={onSubmit}
            initialValues={formState}
            isEditing={Boolean(editingId)}
            loading={loading}
            calculationTypes={calculationTypes}
          />
        )}
      </Modal>
      
      {/* Debug info */}
      {showModal && (
        <div className="fixed bottom-4 right-4 bg-black text-white p-2 text-xs rounded z-50">
          <div>formState: {JSON.stringify(formState)}</div>
          <div>editingId: {editingId}</div>
          <div>formLoading: {formLoading}</div>
        </div>
      )}

      {/* List */}
      <div className="overflow-x-auto bg-white dark:bg-gray-900 shadow-md rounded-lg font-poppins">
        <table className="min-w-full table-auto md:table-fixed text-sm">
          <thead className="bg-gray-100 dark:bg-gray-800 dark:text-white">
            <tr>
              <th className="whitespace-nowrap text-left px-3 py-2 font-semibold">Name</th>
              <th className="whitespace-nowrap text-left px-3 py-2 font-semibold">Type</th>
              <th className="whitespace-nowrap text-left px-3 py-2 font-semibold">Calc</th>
              <th className="whitespace-nowrap text-left px-3 py-2 font-semibold">Taxable</th>
              <th className="whitespace-nowrap text-left px-3 py-2 font-semibold">Active</th>
              <th className="whitespace-nowrap text-left px-3 py-2 font-semibold">Order</th>
              <th className="whitespace-nowrap text-center px-3 py-2 font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {loading ? (
              <tr>
                <td className="px-3 py-3 text-center" colSpan={7}>Loading...</td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td className="px-3 py-3 text-center" colSpan={7}>No configurations found</td>
              </tr>
            ) : (
              items.map((it) => (
                <tr key={it.id} className="border-t dark:border-gray-800">
                  <td className="px-3 py-2">{it.component_name}</td>
                  <td className="px-3 py-2">{it.component_type}</td>
                  <td className="px-3 py-2">{it.calculation_type}</td>
                  <td className="px-3 py-2">{it.is_taxable ? "Yes" : "No"}</td>
                  <td className="px-3 py-2">{it.is_active ? "Yes" : "No"}</td>
                  <td className="px-3 py-2">{it.display_order ?? "-"}</td>
                  <td className="px-3 py-2">
                    <ActionsColumn
                      row={it}
                      openMenu={openMenu}
                      setOpenMenu={setOpenMenu}
                      onEdit={onEdit}
                      onDelete={onDelete}
                      disabled={formLoading}
                    />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PayrollComponents;